'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import type { ReactElement } from 'react';
import { TbChevronLeft, TbHelpCircle } from 'react-icons/tb';

const LAMP_TRANSITION = { delay: 0.3, duration: 0.8, ease: 'easeInOut' as const };

/**
 * Página 404 com o elemento "Lamp" (Aceternity / 21st.dev) portado pro roxo da
 * marca (#9061FF) + fundo do brandbook. Lamp compacto: a linha, o glow e os
 * cones ficam juntos e o "404" encaixa logo abaixo do glow (sem gap). O "404"
 * tem fade progressivo na base (100% topo → 0% base). Animação via framer-motion.
 */
export function NotFoundView(): ReactElement {
  function goBack(): void {
    if (typeof window !== 'undefined') {
      if (window.history.length > 1) window.history.back();
      else window.location.assign('/');
    }
  }

  return (
    <div className="bg-bg relative flex min-h-screen flex-col items-center justify-center overflow-hidden">
      {/* ===== Lamp (Aceternity) compacto · roxo da marca ===== */}
      <div className="relative flex h-[150px] w-full max-w-[760px] justify-center">
        {/* cone esquerdo */}
        <motion.div
          initial={{ opacity: 0.5, width: '15rem' }}
          animate={{ opacity: 1, width: '30rem' }}
          transition={LAMP_TRANSITION}
          style={{
            backgroundImage: 'conic-gradient(from 70deg at center top, #9061FF, transparent 62%)',
          }}
          className="absolute top-[14px] right-1/2 h-36 w-[30rem] overflow-hidden"
        >
          <div className="bg-bg absolute bottom-0 left-0 h-24 w-full [mask-image:linear-gradient(to_top,white,transparent)]" />
          <div className="bg-bg absolute bottom-0 left-0 h-full w-32 [mask-image:linear-gradient(to_right,white,transparent)]" />
        </motion.div>

        {/* cone direito */}
        <motion.div
          initial={{ opacity: 0.5, width: '15rem' }}
          animate={{ opacity: 1, width: '30rem' }}
          transition={LAMP_TRANSITION}
          style={{
            backgroundImage: 'conic-gradient(from 290deg at center top, transparent 38%, #9061FF)',
          }}
          className="absolute top-[14px] left-1/2 h-36 w-[30rem] overflow-hidden"
        >
          <div className="bg-bg absolute right-0 bottom-0 h-full w-32 [mask-image:linear-gradient(to_left,white,transparent)]" />
          <div className="bg-bg absolute right-0 bottom-0 h-24 w-full [mask-image:linear-gradient(to_top,white,transparent)]" />
        </motion.div>

        {/* glow central (bloom roxo) */}
        <div className="bg-violet absolute top-[24px] h-36 w-[28rem] rounded-full opacity-[0.45] blur-[55px]" />

        {/* núcleo (mais claro) */}
        <motion.div
          initial={{ width: '8rem' }}
          animate={{ width: '16rem' }}
          transition={LAMP_TRANSITION}
          className="absolute top-[6px] h-24 w-64 rounded-full bg-[#a884ff] blur-[38px]"
        />

        {/* linha brilhante da lâmpada */}
        <motion.div
          initial={{ width: '15rem' }}
          animate={{ width: '30rem' }}
          transition={LAMP_TRANSITION}
          className="absolute top-[14px] h-0.5 w-[30rem] bg-[#c2a9ff] shadow-[0_0_26px_3px_rgba(144,97,255,0.75)]"
        />
      </div>

      {/* ===== Conteúdo · 404 encaixado logo abaixo do glow (sem gap) ===== */}
      <div className="relative z-[5] -mt-[52px] flex flex-col items-center px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          aria-hidden
          style={{
            backgroundImage: 'linear-gradient(180deg,#EAE7FB 0%,#9061FF 55%,#7A4FE0 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom,#000 30%,transparent 95%)',
            maskImage: 'linear-gradient(to bottom,#000 30%,transparent 95%)',
          }}
          className="bg-clip-text text-[clamp(120px,22vw,260px)] leading-[0.85] font-extrabold tracking-tighter text-transparent select-none"
        >
          404
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-ink -mt-1 text-[22px] font-semibold tracking-tight"
        >
          Essa página não existe
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-ink-muted mt-3 max-w-[420px] text-sm leading-relaxed"
        >
          A página que você tentou acessar não existe ou foi movida. Talvez o link esteja errado, ou
          ela tenha mudado de lugar.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-7 flex flex-wrap items-center justify-center gap-3"
        >
          <button
            type="button"
            onClick={goBack}
            className="border-border-subtle text-ink-muted hover:border-violet/40 hover:text-ink inline-flex items-center gap-2 rounded-xl border px-5 py-3 text-sm font-medium transition"
          >
            <TbChevronLeft size={16} strokeWidth={2} />
            Voltar pra página anterior
          </button>
          <Link
            href={'/ajuda' as never}
            className="from-violet via-magenta to-fuchsia shadow-trakd-glow-violet inline-flex items-center gap-2 rounded-xl bg-gradient-to-br px-5 py-3 text-sm font-semibold text-white transition hover:brightness-110"
          >
            <TbHelpCircle size={16} strokeWidth={2} />
            Acessar central de ajuda
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
